import { SessionData } from './types/sessionTypes';

type __SessionData = SessionData & {
  __session: string;
  __version: string;
};

type __State = {
  id?: string;
  referrer?: string;
};

const DATA_VERSION = '1';

const decodeData = (val: string): __SessionData => {
  return JSON.parse(val);
};

const encodeData = (data: __SessionData): string => {
  return JSON.stringify(data);
};

let state: __State = {};

const hasSession = () => {
  return !!state.id;
};

const keyRef = (): string => {
  return `fitroom.widget.session.${state.referrer}`;
};

/**
 * Init session.
 * @param id Session ID.
 * @param referrer Session Origin.
 */
export const init = (id: string, referrer: string): void => {
  state.id = id;
  state.referrer = referrer;

  // Recreate data when required.
  const key = keyRef();
  const str = localStorage.getItem(key);

  let needsReset = false;

  if (!str) {
    needsReset = true;
  } else {
    const data = decodeData(str);
    if (data.__version !== DATA_VERSION || data.__session !== id) {
      needsReset = true;
    }
  }

  if (needsReset) {
    const data: __SessionData = {
      __session: state.id,
      __version: DATA_VERSION,
    };
    const str = encodeData(data);
    localStorage.setItem(key, str);
  }
};

/**
 * Get session data.
 */
export const getData = (): SessionData | undefined => {
  if (!hasSession()) {
    return undefined;
  }

  const key = keyRef();
  const str = localStorage.getItem(key);

  if (!str) {
    return undefined;
  }

  const data = decodeData(str);
  return data;
};

/**
 * Fill undefined session data fields.
 * @param patch Patch to apply.
 */
export const fillData = (patch: SessionData): void => {
  if (!hasSession()) {
    return;
  }

  const data = getData() as __SessionData;
  if (!data.avatarDisplayCovBottom) {
    data.avatarDisplayCovBottom = patch.avatarDisplayCovBottom;
  }
  if (!data.avatarDisplayCovTop) {
    data.avatarDisplayCovTop = patch.avatarDisplayCovTop;
  }
  if (!data.avatarDisplaySkin) {
    data.avatarDisplaySkin = patch.avatarDisplaySkin;
  }
  if (!data.avatarId) {
    data.avatarId = patch.avatarId;
  }
  if (!data.poseId) {
    data.poseId = patch.poseId;
  }

  const key = keyRef();
  const str = encodeData(data);
  localStorage.setItem(key, str);
};

/**
 * Update session data.
 * @param patch Patch to apply.
 */
export const pushData = (patch: SessionData): void => {
  if (!hasSession()) {
    return;
  }

  const prev = getData() as __SessionData;
  const data = { ...prev, ...patch };

  const key = keyRef();
  const str = encodeData(data);
  localStorage.setItem(key, str);
};
